// Temporary: remove with FormFieldStateReset. The form-style seed before the 868mkgxmj trim
// (6210bc3a^, identical to ec9877b0, the only old seed that reached main). The reset only removes a
// Focus/Error value that still equals what this seed wrote.
import { FORM_CHECKBOX, FORM_INPUT, FORM_RADIO, FORM_SELECT } from "@/constants/DataTypes";
import {
  FORM_STYLE_CHECKBOXES,
  FORM_STYLE_DROPDOWN,
  FORM_STYLE_FIELD,
  FORM_STYLE_RADIOS,
} from "@/constants/FormFieldConstants.mjs";

export const DEFAULT_FORM_FONT = "Montserrat";

const REQUIRED_COLOR = "#D74242";
const ERROR_COLOR = "#E74D3C";

/** @typedef {import("@/types/data/FormStyle.mjs").FormStyle} FormStyle */

/**
 * Builds the default style entry for every form type. `field` is the base entry and carries the
 * whole shape; the other three point at it through their top-level `style` and carry only what
 * their type adds or overrides, leaving the rest to the theme entry chain.
 * @param {string} font
 * @returns {Record<string, FormStyle>}
 */
export const createPreTrimFormStyleDefaults = (font) => ({
  [FORM_STYLE_FIELD]: {
    id: FORM_STYLE_FIELD,
    name: "Field",
    type: FORM_INPUT,
    desktop: {
      control: {
        bgcolor: "#ffffff",
        bC: "#ABABAB",
        bW: 2,
        bS: "solid",
        border: { rad: 6 },
        padding: "14px 14px 14px 14px",
        shadow: { style: "none", sc: "#000000:50", sx: 2, sy: 2, sb: 6 },
        text: { font, size: 16, color: "#000000", placeholderColor: "#ABABAB" },
      },
      label: {
        position: "above",
        requiredPlacement: "after",
        gap: 10,
        width: 120,
        font,
        size: 16,
        color: "#000000",
        requiredColor: REQUIRED_COLOR,
      },
      focus: {
        control: {
          bgcolor: "#ffffff",
          bC: "#82C7E3",
          bW: 2,
          bS: "solid",
          border: { rad: 6 },
          padding: "14px 14px 14px 14px",
          shadow: { style: "none", sc: "#000000:40", sx: 0, sy: 0, sb: 6 },
          text: { color: "#000000", placeholderColor: "#ABABAB", font, size: 16 },
        },
        label: {
          font,
          size: 16,
          color: "#000000",
          requiredColor: REQUIRED_COLOR,
        },
      },
      error: {
        control: {
          bgcolor: "#ffffff",
          bC: ERROR_COLOR,
          bW: 2,
          bS: "solid",
          border: { rad: 6 },
          padding: "14px 14px 14px 14px",
          shadow: { style: "none", sc: `${ERROR_COLOR}:30`, sx: 0, sy: 0, sb: 3 },
          text: { color: "#000000", placeholderColor: "#ABABAB", font, size: 16 },
        },
        label: {
          font,
          size: 16,
          color: ERROR_COLOR,
          requiredColor: REQUIRED_COLOR,
        },
      },
    },
    mobile: {},
    sync: [
      "control.bgcolor",
      "control.bC",
      "control.bW",
      "control.bS",
      "control.border.rad",
      "control.padding",
      "control.shadow.style",
      "control.text.font",
      "control.text.size",
      "control.text.color",
      "control.text.placeholderColor",
      "label.position",
      "label.requiredPlacement",
      "label.gap",
      "label.width",
      "label.font",
      "label.size",
      "label.color",
      "label.requiredColor",
      "focus.control.bgcolor",
      "focus.control.bC",
      "focus.control.bW",
      "focus.control.bS",
      "focus.control.border.rad",
      "focus.control.padding",
      "focus.control.shadow.style",
      "focus.control.text.color",
      "focus.control.text.placeholderColor",
      "focus.control.text.font",
      "focus.control.text.size",
      "focus.label.font",
      "focus.label.size",
      "focus.label.color",
      "focus.label.requiredColor",
      "error.control.bgcolor",
      "error.control.bC",
      "error.control.bW",
      "error.control.bS",
      "error.control.border.rad",
      "error.control.padding",
      "error.control.shadow.style",
      "error.control.text.color",
      "error.control.text.placeholderColor",
      "error.control.text.font",
      "error.control.text.size",
      "error.label.font",
      "error.label.size",
      "error.label.color",
      "error.label.requiredColor",
    ],
  },

  [FORM_STYLE_DROPDOWN]: {
    id: FORM_STYLE_DROPDOWN,
    name: "Dropdown",
    type: FORM_SELECT,
    style: FORM_STYLE_FIELD,
    desktop: {
      control: {
        padding: "14px 40px 14px 14px",
        arrow: { style: "chevron", color: "#000000", size: 22 },
      },
      focus: {
        control: {
          bgcolor: "#ffffff",
          padding: "14px 40px 14px 14px",
          arrow: { color: "#000000", size: 22 },
        },
      },
      error: {
        control: {
          bgcolor: "#ffffff",
          padding: "14px 40px 14px 14px",
          arrow: { color: "#000000", size: 22 },
        },
      },
    },
    mobile: {},
    sync: [
      "control.padding",
      "control.arrow.style",
      "control.arrow.color",
      "control.arrow.size",
      "focus.control.bgcolor",
      "focus.control.padding",
      "focus.control.arrow.color",
      "focus.control.arrow.size",
      "error.control.bgcolor",
      "error.control.padding",
      "error.control.arrow.color",
      "error.control.arrow.size",
    ],
  },

  [FORM_STYLE_RADIOS]: {
    id: FORM_STYLE_RADIOS,
    name: "Radios",
    type: FORM_RADIO,
    style: FORM_STYLE_FIELD,
    desktop: {
      control: {
        bgcolor: "#ffffff",
        bW: 1,
        border: { rad: "50%" },
        padding: "3px 3px 3px 3px",
        radio: { style: "filled", dotColor: "#000000", dotSize: 6 },
      },
      radio: { direction: "vertical", gap: 14 },
      focus: {
        control: {
          bgcolor: "#ffffff",
          bW: 1,
          border: { rad: "50%" },
          padding: "3px 3px 3px 3px",
          shadow: { style: "none", sc: "#000000:20", sx: 0, sy: 1, sb: 3 },
          radio: { dotColor: "#000000", dotSize: 6 },
        },
      },
      error: {
        control: {
          bgcolor: "#ffffff",
          bW: 1,
          border: { rad: "50%" },
          padding: "3px 3px 3px 3px",
          radio: { dotColor: "#000000", dotSize: 6 },
        },
      },
    },
    mobile: {},
    sync: [
      "control.bgcolor",
      "control.bW",
      "control.border.rad",
      "control.padding",
      "radio.direction",
      "radio.gap",
      "control.radio.style",
      "control.radio.dotColor",
      "control.radio.dotSize",
      "focus.control.bgcolor",
      "focus.control.bW",
      "focus.control.border.rad",
      "focus.control.padding",
      "focus.control.shadow.style",
      "focus.control.radio.dotColor",
      "focus.control.radio.dotSize",
      "error.control.bgcolor",
      "error.control.bW",
      "error.control.border.rad",
      "error.control.padding",
      "error.control.radio.dotColor",
      "error.control.radio.dotSize",
    ],
  },

  [FORM_STYLE_CHECKBOXES]: {
    id: FORM_STYLE_CHECKBOXES,
    name: "Checkboxes",
    type: FORM_CHECKBOX,
    style: FORM_STYLE_FIELD,
    desktop: {
      control: {
        bgcolor: "#ffffff",
        border: { rad: 4 },
        checkbox: { style: "checkmark" },
      },
      label: { position: "inline" },
      focus: {
        control: {
          bgcolor: "#ffffff",
          border: { rad: 4 },
          shadow: { style: "none", sc: "#000000:25", sx: 0, sy: 0, sb: 4 },
        },
      },
      error: {
        control: {
          bgcolor: "#ffffff",
          border: { rad: 4 },
        },
      },
    },
    mobile: {},
    sync: [
      "control.bgcolor",
      "control.border.rad",
      "control.checkbox.style",
      "label.position",
      "focus.control.bgcolor",
      "focus.control.border.rad",
      "focus.control.shadow.style",
      "error.control.bgcolor",
      "error.control.border.rad",
    ],
  },
});

export default createPreTrimFormStyleDefaults;
